import { useGetUserCalenderInfo } from '@forwardflow/hooks/useGetUserCalenderInfo'
import { UserService } from '@forwardflow/services/UserService'
import FubInboxIntegration from '../FubInboxIntegration'
import GoogleCalendarIcon from '../../../assets/svg/GoogleCalendarIcon'
import { useAppStore } from '@forwardflow/store'
import { useCalendarModal } from '@forwardflow/store/modal/calendarModal'
import { CalendarModal } from '@forwardflow/modals/CalendarModal'
import { useMemo } from 'react'
import { useGetCalendarList } from '@forwardflow/hooks/customHooks/useGetCalendarList'
import Icon from '@forwardflow/ui/Icons'
import { Tooltip } from 'antd'

const CalendarIntegration = () => {
   const { currentOrganization } = useAppStore()
   const { data: UserCalenderInfo, mutate: mutateUserCalendarInfo } = useGetUserCalenderInfo(
      !!currentOrganization
   )
   const { openCalendarModal } = useCalendarModal()
   const isCalendarConnected = !!(UserCalenderInfo && UserCalenderInfo.email)
   const { data: calendars, mutate: mutateCalendars } = useGetCalendarList(isCalendarConnected)

   const linkedCalendars = useMemo(() => {
      if (!UserCalenderInfo?.preferredCalendars || UserCalenderInfo.preferredCalendars.length === 0) {
         return []
      }

      return UserCalenderInfo.preferredCalendars
   }, [UserCalenderInfo?.preferredCalendars])

   const handleCalendarUpdate = async () => {
      await mutateCalendars()
      await mutateUserCalendarInfo()
   }

   const handleCalendarConnect = async () => {
      try {
         const redirectUrl = encodeURIComponent(
            `${window.location.origin}/settings/integration`
         )
         const response = await UserService.getConnectUserWithCalender(
            redirectUrl,
            currentOrganization || ''
         )

         if (response.redirectUrl) {
            window.location.href = response.redirectUrl
         }
      } catch (e) {
         console.error('Error connecting calendar:', e)
      }
   }

   return (
      <div className="flex flex-col items-center min-h-screen px-8 pt-6 ">
         <div className="w-full max-w-xl">
            <h2 className="mb-2 font-semibold text-white">Integration</h2>
            <p className="mb-16 text-sm text-white/70">
               Connect your tools to Forward Flow for seamless scheduling and
               communication.
            </p>
            <div className={`flex flex-col gap-8 p-6 rounded-lg bg-[#828282]/10 shadow-lg ${isCalendarConnected ? '' : 'max-w-96'}`}>
               <div className="flex gap-14 items-center justify-between mb-6">
                  <div>
                     <p className="mb-4 font-medium text-white">
                        {isCalendarConnected
                           ? 'Google Calendar connected'
                           : 'Google Calendar not connected'}
                     </p>
                     <p className="text-sm text-white/70">
                        {isCalendarConnected
                           ? `Connected to: ${UserCalenderInfo.email}`
                           : 'Connect your Google Calendar so Forward Flow can help schedule appointments.'}
                     </p>
                  </div>
                  {isCalendarConnected && (
                     <button
                        onClick={handleCalendarConnect}
                        className="flex items-center h-10 gap-6 px-4 py-2 text-sm font-medium text-white transition-colors rounded-md bg-white/10 hover:bg-white/20 text-nowrap"
                     >
                        <GoogleCalendarIcon />
                        Change Calendar
                        <Tooltip
                           title={
                              <div className="text-xs">
                                 Changing your calendar integration won't affect your login account.
                                 You'll still sign in the same way, this only updates which calendar
                                 Forward Flow uses for scheduling.
                              </div>
                           }
                           placement="top"
                           overlayInnerStyle={{
                              backgroundColor: '#1E1E1E',
                              borderRadius: '8px',
                              padding: '16px',
                              width: '300px',
                           }}
                        >
                           <div onClick={(e) => e.stopPropagation()} className="cursor-pointer">
                              <Icon name="Info" width={12} />
                           </div>
                        </Tooltip>
                     </button>
                  )}
               </div>
               {isCalendarConnected && (
                  <div className="border-t border-white/20 pt-6">
                     <div className="flex items-center justify-between mb-2">
                        <p className="font-normal text-white text-sm">
                           Linked Calendars
                        </p>
                        {calendars && calendars.length > 0 && (
                           <button
                              onClick={openCalendarModal}
                              className="px-8 py-2 text-sm font-medium text-white bg-white/10 rounded-md hover:bg-white/20"
                           >
                              {linkedCalendars.length > 0 ? 'Edit' : 'Add'}
                           </button>
                        )}
                     </div>
                     <p className="text-xs text-white/70 mb-4">
                        Connect calendars from your Google account to enable availability checks from them.
                     </p>

                     {linkedCalendars.length > 0 ? (
                        <div className="flex flex-wrap gap-2 mb-4">
                           {linkedCalendars.map((calendar: any) => {
                              const isDefaultCalendar =
                                 calendar.id?.toLowerCase() === 'primary' ||
                                 calendar.summary?.toLowerCase() === 'primary' ||
                                 calendar.id?.toLowerCase() === UserCalenderInfo?.email?.toLowerCase() ||
                                 calendar.summary?.toLowerCase() === UserCalenderInfo?.email?.toLowerCase()
                              const displayName = isDefaultCalendar ? 'Default' : calendar.summary
                              return (
                                 <span
                                    key={calendar.id}
                                    className="px-3 py-1.5 text-xs font-medium text-white bg-white/10 rounded-md"
                                 >
                                    {displayName}
                                 </span>
                              )
                           })}
                        </div>
                     ) : (
                        <div className="text-sm text-white/60 italic">
                           No calendars linked yet
                        </div>
                     )}
                  </div>
               )}
               {!isCalendarConnected && (
                  <button
                     onClick={handleCalendarConnect}
                     className="flex items-center h-10 gap-2 px-4 py-2 font-medium text-white transition-colors rounded-md bg-white/10 hover:bg-white/20"
                  >
                     <GoogleCalendarIcon />
                     Connect Google Calendar
                  </button>
               )}
            </div>
            <FubInboxIntegration />
         </div>
         <CalendarModal
            onCalendarUpdate={handleCalendarUpdate}
            linkedCalendars={linkedCalendars}
            connectedEmail={UserCalenderInfo?.email}
         />
      </div>
   )
}

export default CalendarIntegration
